import type { Ref } from 'react'
import { NavLink } from 'react-router-dom'
import { ApiStatusBadge } from '@/components/api-status-badge'
import { LogoMark, Wordmark } from '@/components/logo'
import { NavIcon, type NavIconName } from '@/components/nav-icons'
import { ThemeToggle } from '@/components/theme-toggle'
import { SessionBadge } from '@/features/auth/session-badge'

const NAV_ITEMS: { to: string; label: string; icon: NavIconName }[] = [
  { to: '/checklists', label: 'Unterlagen', icon: 'checklists' },
  { to: '/protokolle', label: 'Übergabeprotokolle', icon: 'handovers' },
  { to: '/briefings', label: 'Briefings', icon: 'briefings' },
  { to: '/bild-ki', label: 'Bild-KI', icon: 'imageAi' },
  { to: '/dashboard', label: 'Übersicht', icon: 'dashboard' },
  { to: '/system', label: 'System', icon: 'system' },
  { to: '/settings', label: 'Einstellungen', icon: 'settings' },
]

/**
 * The navigation, in its two bodies.
 *
 * `rail` is the desktop column, which FOLDS to a 56px icon strip — the document
 * matrix wants every pixel of width, and 23 rotated column headers are a better
 * use of 184 of them than the word "Einstellungen". `drawer` is the same thing
 * on a phone, slid in over the page from the header's menu button. Below `sm`
 * the rail is not rendered, and until the drawer existed that meant a phone had
 * NO navigation at all: every page was reachable by typing its address and no
 * other way, in a tool whose protocols are written standing in a stairwell.
 *
 * One component rather than two because the contents must never diverge — a
 * page added to one list and not the other is a page that exists on half the
 * devices.
 */
export function Sidebar({
  variant,
  collapsed = false,
  onToggle,
  onNavigate,
  open = false,
  id,
  ref,
}: {
  variant: 'rail' | 'drawer'
  /** Folded to the icon strip. Only the rail folds; the drawer opens and closes. */
  collapsed?: boolean
  /** Rail: fold or unfold. Drawer: close. */
  onToggle: () => void
  /** Drawer only — a tap on a link has to take the overlay away with it. */
  onNavigate?: () => void
  /** Drawer only. A parked drawer must cast no shadow — see the class list. */
  open?: boolean
  id: string
  ref?: Ref<HTMLElement>
}) {
  const isDrawer = variant === 'drawer'

  const toggleLabel = isDrawer
    ? 'Navigation schließen'
    : collapsed
      ? 'Seitenleiste ausklappen'
      : 'Seitenleiste einklappen'

  const toggle = (
    <button
      type="button"
      onClick={onToggle}
      aria-label={toggleLabel}
      // Drawer: the button CLOSES a dialog, it does not disclose anything.
      aria-expanded={isDrawer ? undefined : !collapsed}
      aria-controls={isDrawer ? undefined : id}
      title={isDrawer ? toggleLabel : `${toggleLabel} (Strg/⌘ B)`}
      className={[
        'text-faint hover:text-ink hover:bg-surface/70 shrink-0 transition-colors',
        // The wordmark is 179px of a 208px content box. A 36px button beside it
        // overflowed into the rail's padding and was clipped at the border, so
        // on the desk it is 28px and borrows 6 of them from the padding. The
        // drawer is wider and is pressed with a thumb: it keeps the full size.
        isDrawer ? 'p-2.5' : collapsed ? 'p-1.5' : '-mr-1.5 p-1.5',
      ].join(' ')}
    >
      <NavIcon name={isDrawer ? 'close' : 'panel'} />
    </button>
  )

  return (
    <aside
      id={id}
      ref={ref}
      // The drawer takes focus ITSELF when it opens (tabIndex -1), not its close
      // button: a focus ring appearing on "schließen" the moment the menu opens
      // reads as the app suggesting you leave again.
      {...(isDrawer
        ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Navigation', tabIndex: -1 }
        : {})}
      className={[
        // Sand rail against the white worksurface — the public site's secondary
        // tone, doing the job of separating navigation from data.
        //
        // `overflow-hidden` is what makes the fold a wipe: for the 200ms the
        // width travels, labels are CLIPPED at the edge. Without it they wrap
        // to three lines and the rail shudders on the way.
        'bg-raised border-line flex shrink-0 flex-col overflow-hidden border-r py-6',
        isDrawer
          ? // The shadow only while open. Translated off-screen the PANEL is
            // gone but its shadow is not — it reached 20px back into the page
            // as a grey smear down the left edge of every phone screen.
            `h-full w-64 max-w-[85vw] px-4 outline-none ${open ? 'shadow-xl' : ''}`
          : [
              'hidden transition-[width,padding] duration-200 ease-out sm:flex',
              collapsed ? 'w-14 px-2' : 'w-60 px-4',
            ].join(' '),
      ].join(' ')}
    >
      {collapsed ? (
        <div className="flex flex-col items-center gap-3">
          <LogoMark className="h-6 w-auto" />
          {toggle}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-1 pl-1 whitespace-nowrap">
          <Wordmark />
          {toggle}
        </div>
      )}

      <nav className={collapsed ? 'mt-6 flex flex-col gap-0.5' : 'mt-9 flex flex-col gap-0.5'}>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            // Folded, the label is invisible to a pointer; the tooltip is its
            // stand-in. The text itself stays in the DOM (see below), so the
            // link never loses its accessible name.
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              [
                // The 2px rule is the active marker; the tint only supports it.
                // 11px, not px-3: 2 + 11 + 16 + 11 = 40, the folded link's full
                // width, which is what centres the icon in the strip.
                'flex items-center gap-3 border-l-2 px-[11px] whitespace-nowrap transition-colors',
                isDrawer ? 'py-3 text-sm' : 'py-2 text-[13px]',
                isActive
                  ? 'border-brand bg-brand-soft text-brand font-medium'
                  : 'text-muted hover:text-ink hover:border-line-strong hover:bg-surface/70 border-transparent',
              ].join(' ')
            }
          >
            <NavIcon name={item.icon} />
            {/* Faded, not unmounted: removing it would leave a screen reader
                with six unnamed links the moment the rail is folded. */}
            <span className={`transition-opacity duration-150 ${collapsed ? 'opacity-0' : ''}`}>
              {item.label}
            </span>
          </NavLink>
        ))}
      </nav>

      {collapsed ? (
        // No theme control here: three segments do not fit in 40px, and it has a
        // labelled home on the Einstellungen page one click away.
        <div className="border-line mt-auto flex flex-col items-center gap-3 border-t pt-4">
          <SessionBadge compact />
          <ApiStatusBadge compact />
        </div>
      ) : (
        <div className="border-line mt-auto flex flex-col gap-3 border-t px-2 pt-4 whitespace-nowrap">
          <SessionBadge />
          <ApiStatusBadge />
          <ThemeToggle />
        </div>
      )}
    </aside>
  )
}
